import { supabase } from '../supabaseClient';

// Helper function to get start date based on period
const getStartDate = (period = 'month') => {
  const now = new Date();
  const startDate = new Date();

  switch (period) {
    case 'week':
      startDate.setDate(now.getDate() - 7);
      break;
    case 'month':
      startDate.setMonth(now.getMonth() - 1);
      break;
    case 'year':
      startDate.setFullYear(now.getFullYear() - 1);
      break;
    default:
      startDate.setMonth(now.getMonth() - 1);
  }

  return startDate.toISOString();
};

// Helper function to get previous period start date
const getPreviousStartDate = (period = 'month') => {
  const now = new Date();
  const startDate = new Date();

  switch (period) {
    case 'week':
      startDate.setDate(now.getDate() - 14);
      break;
    case 'month':
      startDate.setMonth(now.getMonth() - 2);
      break;
    case 'year':
      startDate.setFullYear(now.getFullYear() - 2);
      break;
    default:
      startDate.setMonth(now.getMonth() - 2);
  }

  return startDate.toISOString();
};

/**
 * Get teacher's total earnings and percentage change
 * @param {string} teacherEmail - Teacher's email address
 * @param {string} period - Time period ('week', 'month', 'year')
 * @returns {Promise<{total: number, change: number}>}
 */
export async function getTeacherEarnings(teacherEmail, period = 'month') {
  try {
    const currentStart = getStartDate(period);
    const previousStart = getPreviousStartDate(period);

    // Current period earnings
    const { data: currentEarnings, error: currentError } = await supabase
      .from('booking_requests')
      .select('total_price')
      .eq('teacher_email', teacherEmail)
      .in('status', ['completed', 'accepted'])
      .gte('created_at', currentStart);

    if (currentError) throw currentError;

    // Previous period earnings
    const { data: previousEarnings, error: previousError } = await supabase
      .from('booking_requests')
      .select('total_price')
      .eq('teacher_email', teacherEmail)
      .in('status', ['completed', 'accepted'])
      .gte('created_at', previousStart)
      .lt('created_at', currentStart);

    if (previousError) throw previousError;

    // Calculate totals
    const current = currentEarnings?.reduce((sum, r) => sum + (parseFloat(r.total_price) || 0), 0) || 0;
    const previous = previousEarnings?.reduce((sum, r) => sum + (parseFloat(r.total_price) || 0), 0) || 0;

    // Calculate percentage change
    const change = previous > 0 ? ((current - previous) / previous) * 100 : (current > 0 ? 100 : 0);

    return { total: current, change };
  } catch (error) {
    console.error('Error fetching teacher earnings:', error);
    return { total: 0, change: 0 };
  }
}

/**
 * Get count of active students and percentage change
 * @param {string} teacherEmail - Teacher's email address
 * @param {string} period - Time period ('week', 'month', 'year')
 * @returns {Promise<{count: number, change: number}>}
 */
export async function getActiveStudents(teacherEmail, period = 'month') {
  try {
    const currentStart = getStartDate(period);
    const previousStart = getPreviousStartDate(period);

    // Current period unique students
    const { data: currentStudents, error: currentError } = await supabase
      .from('booking_requests')
      .select('student_email')
      .eq('teacher_email', teacherEmail)
      .in('status', ['accepted', 'completed'])
      .gte('created_at', currentStart);

    if (currentError) throw currentError;

    // Previous period unique students
    const { data: previousStudents, error: previousError } = await supabase
      .from('booking_requests')
      .select('student_email')
      .eq('teacher_email', teacherEmail)
      .in('status', ['accepted', 'completed'])
      .gte('created_at', previousStart)
      .lt('created_at', currentStart);

    if (previousError) throw previousError;

    // Count unique students
    const currentCount = new Set(currentStudents?.map(r => r.student_email) || []).size;
    const previousCount = new Set(previousStudents?.map(r => r.student_email) || []).size;

    // Calculate percentage change
    const change = previousCount > 0 ? ((currentCount - previousCount) / previousCount) * 100 : (currentCount > 0 ? 100 : 0);

    return { count: currentCount, change };
  } catch (error) {
    console.error('Error fetching active students:', error);
    return { count: 0, change: 0 };
  }
}

/**
 * Get upcoming sessions for the teacher
 * @param {string} teacherEmail - Teacher's email address
 * @param {number} limit - Maximum number of sessions to return
 * @returns {Promise<{data: Array, error: any}>}
 */
export async function getUpcomingSessions(teacherEmail, limit = 5) {
  try {
    const today = new Date().toISOString().split('T')[0];

    const { data, error } = await supabase
      .from('booking_requests')
      .select('id, date, time_from, time_to, subject, student_name, student_email, request_type')
      .eq('teacher_email', teacherEmail)
      .eq('status', 'accepted')
      .gte('date', today)
      .order('date', { ascending: true })
      .order('time_from', { ascending: true })
      .limit(limit);

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching upcoming sessions:', error);
    return { data: [], error };
  }
}

/**
 * Get earnings history data for chart
 * @param {string} teacherEmail - Teacher's email address
 * @param {number} days - Number of days to look back
 * @returns {Promise<{data: Array, error: any}>}
 */
export async function getEarningsHistory(teacherEmail, days = 30) {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from('booking_requests')
      .select('created_at, total_price, date')
      .eq('teacher_email', teacherEmail)
      .in('status', ['completed', 'accepted'])
      .gte('created_at', startDate.toISOString());

    if (error) throw error;

    // Group by date and sum earnings
    const grouped = {};
    data?.forEach(booking => {
      const day = booking.date || booking.created_at.split('T')[0];
      grouped[day] = (grouped[day] || 0) + (parseFloat(booking.total_price) || 0);
    });

    // Convert to array and sort by date
    const chartData = Object.entries(grouped)
      .map(([date, earnings]) => ({
        date,
        earnings,
        day: new Date(date).toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    // Fill in missing days with 0 earnings
    const filledData = [];
    const endDate = new Date();
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      const existingData = chartData.find(d => d.date === dateStr);

      filledData.push({
        date: dateStr,
        earnings: existingData ? existingData.earnings : 0,
        day: currentDate.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return { data: filledData, error: null };
  } catch (error) {
    console.error('Error fetching earnings history:', error);
    return { data: [], error };
  }
}

/**
 * Get profile views count (estimated)
 * Note: This is an estimation based on booking requests
 * @param {string} teacherEmail - Teacher's email address
 * @param {string} period - Time period ('week', 'month', 'year')
 * @returns {Promise<{count: number, change: number}>}
 */
export async function getProfileViews(teacherEmail, period = 'month') {
  try {
    const currentStart = getStartDate(period);
    const previousStart = getPreviousStartDate(period);

    // Current period booking requests (as proxy for profile views)
    const { data: currentRequests, error: currentError } = await supabase
      .from('booking_requests')
      .select('id', { count: 'exact' })
      .eq('teacher_email', teacherEmail)
      .gte('created_at', currentStart);

    if (currentError) throw currentError;

    // Previous period booking requests
    const { data: previousRequests, error: previousError } = await supabase
      .from('booking_requests')
      .select('id', { count: 'exact' })
      .eq('teacher_email', teacherEmail)
      .gte('created_at', previousStart)
      .lt('created_at', currentStart);

    if (previousError) throw previousError;

    // Estimate views (multiply by 4 as a rough approximation)
    const currentViews = (currentRequests?.length || 0) * 4;
    const previousViews = (previousRequests?.length || 0) * 4;

    // Calculate percentage change
    const change = previousViews > 0 ? ((currentViews - previousViews) / previousViews) * 100 : (currentViews > 0 ? 100 : 0);

    return { count: currentViews, change };
  } catch (error) {
    console.error('Error fetching profile views:', error);
    return { count: 0, change: 0 };
  }
}

/**
 * Fetch all dashboard data in parallel
 * @param {string} teacherEmail - Teacher's email address
 * @param {string} timeFilter - Time filter for earnings history ('7d', '30d', '90d', '1y')
 * @returns {Promise<Object>} All dashboard metrics
 */
export async function fetchAllDashboardData(teacherEmail, timeFilter = '30d') {
  try {
    // Convert time filter to days
    const daysMap = {
      '7d': 7,
      '30d': 30,
      '90d': 90,
      '1y': 365
    };
    const days = daysMap[timeFilter] || 30;

    // Fetch all data in parallel
    const [earnings, students, sessions, history, views] = await Promise.all([
      getTeacherEarnings(teacherEmail, 'month'),
      getActiveStudents(teacherEmail, 'month'),
      getUpcomingSessions(teacherEmail, 5),
      getEarningsHistory(teacherEmail, days),
      getProfileViews(teacherEmail, 'month')
    ]);

    return {
      earnings,
      students,
      upcomingSessions: sessions.data,
      earningsHistory: history.data,
      profileViews: views
    };
  } catch (error) {
    console.error('Error fetching all dashboard data:', error);
    return {
      earnings: { total: 0, change: 0 },
      students: { count: 0, change: 0 },
      upcomingSessions: [],
      earningsHistory: [],
      profileViews: { count: 0, change: 0 }
    };
  }
}
